/* utopia-nav.js — Utopia main navigation
   Owns hover intent, click, keyboard and the mobile drilldown.
   Deliberately does NOT use Bootstrap's dropdown API. No jQuery. */
(function () {
  'use strict';

  var OPEN_DELAY = 120;
  var CLOSE_DELAY = 200;

  function canHover() {
    return window.matchMedia('(hover: hover)').matches && window.innerWidth >= 992;
  }

  function focusables(el) {
    return Array.prototype.slice.call(
      el.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
    );
  }

  function closestFrom(node, selector, boundary) {
    while (node && node !== boundary.parentNode) {
      if (node.nodeType === 1 && node.matches && node.matches(selector)) { return node; }
      node = node.parentNode;
    }
    return null;
  }

  /* ------------------------------------------------------------------ *
   * Desktop mega menu
   * ------------------------------------------------------------------ */
  function initBar(root) {
    var items = Array.prototype.slice.call(root.querySelectorAll('[data-un-item]'));
    if (!items.length) { return; }

    var openTimer = null;
    var closeTimer = null;
    var openItem = null;

    function panelOf(item) { return item.querySelector('[data-un-panel]'); }
    function triggerOf(item) { return item.querySelector('[data-un-caret]'); }

    function close(item) {
      if (!item) { return; }
      var panel = panelOf(item);
      var trigger = triggerOf(item);
      item.classList.remove('is-open');
      if (panel) { panel.classList.remove('is-open'); }
      if (trigger) { trigger.setAttribute('aria-expanded', 'false'); }
      if (openItem === item) { openItem = null; }
    }

    function closeAll() { items.forEach(close); }

    function open(item) {
      if (!item || item === openItem) { return; }
      var panel = panelOf(item);
      if (!panel) { return; }
      closeAll();
      item.classList.add('is-open');
      panel.classList.add('is-open');
      var trigger = triggerOf(item);
      if (trigger) { trigger.setAttribute('aria-expanded', 'true'); }
      openItem = item;
    }

    function toggle(item) {
      if (item === openItem) { close(item); } else { open(item); }
    }

    items.forEach(function (item) {
      var panel = panelOf(item);
      var trigger = triggerOf(item);
      if (!panel) { return; }

      item.addEventListener('mouseenter', function () {
        if (!canHover()) { return; }
        window.clearTimeout(closeTimer);
        window.clearTimeout(openTimer);
        openTimer = window.setTimeout(function () { open(item); }, OPEN_DELAY);
      });

      item.addEventListener('mouseleave', function () {
        if (!canHover()) { return; }
        window.clearTimeout(openTimer);
      });

      if (trigger) {
        trigger.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          window.clearTimeout(openTimer);
          window.clearTimeout(closeTimer);
          toggle(item);
        });
      }

      item.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown' || e.keyCode === 40) {
          e.preventDefault();
          open(item);
          var f = focusables(panel);
          if (f.length) { f[0].focus(); }
        }
      });
    });

    root.addEventListener('mouseleave', function () {
      if (!canHover()) { return; }
      window.clearTimeout(openTimer);
      closeTimer = window.setTimeout(closeAll, CLOSE_DELAY);
    });

    root.addEventListener('mouseenter', function () {
      window.clearTimeout(closeTimer);
    });

    root.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.keyCode === 27) {
        if (!openItem) { return; }
        var trigger = triggerOf(openItem);
        close(openItem);
        if (trigger) { trigger.focus(); }
      }
    });

    var topLinks = Array.prototype.slice.call(root.querySelectorAll('[data-un-toplink]'));
    topLinks.forEach(function (link, i) {
      link.addEventListener('keydown', function (e) {
        var next = null;
        if (e.key === 'ArrowRight' || e.keyCode === 39) { next = topLinks[i + 1]; }
        if (e.key === 'ArrowLeft' || e.keyCode === 37) { next = topLinks[i - 1]; }
        if (next) { e.preventDefault(); next.focus(); }
      });
    });

    root.addEventListener('focusout', function () {
      window.setTimeout(function () {
        if (!root.contains(document.activeElement)) { closeAll(); }
      }, 0);
    });

    document.addEventListener('click', function (e) {
      if (!root.contains(e.target)) { closeAll(); }
    });

    window.addEventListener('resize', function () {
      if (window.innerWidth < 992) { closeAll(); }
    });
  }

  /* ------------------------------------------------------------------ *
   * Mobile drilldown
   *
   * Panels are in normal flow and only the active one is displayed, so this
   * never measures anything. An earlier version set an explicit stage height
   * from offsetHeight, which read zero because the drawer sits inside a
   * Bootstrap collapse that is display:none until it opens.
   * ------------------------------------------------------------------ */
  function initDrilldown(root) {
    var stage = root.querySelector('[data-un-stage]');
    if (!stage) { return; }
    var panels = Array.prototype.slice.call(stage.querySelectorAll('[data-un-dd-panel]'));
    if (!panels.length) { return; }

    var stack = ['root'];

    function panelById(id) {
      for (var i = 0; i < panels.length; i++) {
        if (panels[i].getAttribute('data-un-dd-panel') === id) { return panels[i]; }
      }
      return null;
    }

    function render(goingBack) {
      var activeId = stack[stack.length - 1];
      panels.forEach(function (p) {
        var isActive = p.getAttribute('data-un-dd-panel') === activeId;
        p.classList.remove('is-active', 'is-back');
        if (isActive) {
          p.classList.add('is-active');
          if (goingBack) { p.classList.add('is-back'); }
          p.removeAttribute('aria-hidden');
        } else {
          p.setAttribute('aria-hidden', 'true');
        }
      });
    }

    stage.addEventListener('click', function (e) {
      var opener = closestFrom(e.target, '[data-un-dd-open]', stage);
      if (opener) {
        e.preventDefault();
        stack.push(opener.getAttribute('data-un-dd-open'));
        render(false);
        var target = panelById(stack[stack.length - 1]);
        if (target) {
          var f = focusables(target);
          if (f.length) { f[0].focus(); }
        }
        return;
      }
      var back = closestFrom(e.target, '[data-un-dd-back]', stage);
      if (back) {
        e.preventDefault();
        if (stack.length > 1) { stack.pop(); }
        render(true);
      }
    });

    root.addEventListener('keydown', function (e) {
      if ((e.key === 'Escape' || e.keyCode === 27) && stack.length > 1) {
        e.stopPropagation();
        stack.pop();
        render(true);
      }
    });

    function reset() {
      stack = ['root'];
      render(false);
    }

    render(false);

    /* Back to the root panel each time the drawer is opened. */
    var openers = document.querySelectorAll('[data-un-drawer-open]');
    Array.prototype.forEach.call(openers, function (btn) {
      btn.addEventListener('click', function () {
        window.setTimeout(reset, 10);
      });
    });

    var collapse = root.closest ? root.closest('.collapse') : null;
    if (collapse) {
      collapse.addEventListener('hidden.bs.collapse', reset);
    }
  }

  function boot() {
    var bar = document.querySelector('[data-un-root]');
    if (bar) { initBar(bar); }
    var dd = document.querySelector('[data-un-dd]');
    if (dd) { initDrilldown(dd); }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
